import React from 'react';
import cn from 'classnames';
import createPaginationArray from '@bosch/react-frok/lib/atoms/pageIndicator/functions/createPaginationArray';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {TemplatePropertySettings} from '../../models/TemplatePropertySettings';
import {getDefaultValueOfComponent} from '../../utils/TemplateUtils';
import {
    PAGE_INDICATOR_MIN_VISIBLE_ITEMS,
    PAGE_INDICATOR_MOBILE_MIN_VISIBLE_ITEMS
} from '../../init/ui_component_constants';
import {
    defaultPageIndicatorMinVisibleItems,
    defaultPageIndicatorMobileMinVisibleItems
} from '../../init/ui_component_defaults';

export interface PageIndicatorProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly isMobileResolution: boolean;
    readonly pages: number;
    readonly selected: number;
    onPageSelect: (page: number) => void;
}

/**
 * @name PageIndicator
 * @description
 * A page indicator with the possibility to navigate between pages
 */
export const PageIndicator: React.VFC<PageIndicatorProps> = (props) => {
    const leftArrowEnabled = !(props.selected <= 1);
    const leftArrowClasses = cn('a-page-indicator__caret -left', {
        '-end': !leftArrowEnabled
    });

    const rightArrowEnabled = !(props.selected >= props.pages);
    const rightArrowClasses = cn('a-page-indicator__caret -right', {
        '-end': !rightArrowEnabled
    });

    let minVisibleItems = 0;
    if (props.isMobileResolution) {
        minVisibleItems = Number(getDefaultValueOfComponent(PAGE_INDICATOR_MOBILE_MIN_VISIBLE_ITEMS, props.templatePropertySettings));
        if ((minVisibleItems === undefined) || (!minVisibleItems) || isNaN(minVisibleItems) || (minVisibleItems <= 0)) {
            minVisibleItems = defaultPageIndicatorMobileMinVisibleItems;
        }
    } else {
        minVisibleItems = Number(getDefaultValueOfComponent(PAGE_INDICATOR_MIN_VISIBLE_ITEMS, props.templatePropertySettings));
        if ((minVisibleItems === undefined) || (!minVisibleItems) || isNaN(minVisibleItems) || (minVisibleItems <= 0)) {
            minVisibleItems = defaultPageIndicatorMinVisibleItems;
        }
    }

    let pageArray = createPaginationArray(props.selected, props.pages, 1, minVisibleItems);

    return (
        <div id={generateIdentifier()}
             className='a-page-indicator a-page-indicator--numbered'
             data-start-index={props.selected}
             data-max-length={props.pages}
             aria-label='Page indicator'>
            <div id={generateIdentifier()}
                 className={leftArrowClasses}
                 onClick={() => {
                     if (leftArrowEnabled) props.onPageSelect(props.selected - 1);
                 }}/>
            <div id={generateIdentifier()} className='a-page-indicator__container'>
                {pageArray?.map((page, index) => {
                    const indicatorClass = cn('a-page-indicator__indicator', {'-selected': props.selected === page});
                    return (
                        <div id={generateIdentifier()}
                             className={indicatorClass}
                             key={index}
                             data-index={typeof page === 'number' ? page : null}
                             onClick={(event) => {
                                 props.onPageSelect(Number(event.currentTarget.getAttribute('data-index')));
                             }}>
                            <span id={generateIdentifier()}>{page}</span>
                        </div>
                    );
                })}
            </div>
            <div id={generateIdentifier()}
                 className={rightArrowClasses}
                 onClick={() => {
                     if (rightArrowEnabled) props.onPageSelect(props.selected + 1);
                 }}
            />
        </div>
    );
};
